const DAY_MS = 24 * 60 * 60 * 1000

export function calendarEventsFor(element) {
  const source = element.closest("[data-calendar-modal-sessions-value]")
  if (!source) return []

  try {
    return JSON.parse(source.dataset.calendarModalSessionsValue || "[]")
  } catch {
    return []
  }
}

export function upcomingCalendarEvents(events) {
  const now = Date.now()
  return events.filter((event) => {
    const startTime = Date.parse(event.start_time || "")
    if (!Number.isNaN(startTime)) return startTime >= now
    return String(event.calendar_date || "").slice(0, 10) >= todayIso()
  })
}

export function parseMonth(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})/)
  if (!match) {
    const today = new Date()
    return { year: today.getFullYear(), month: today.getMonth() }
  }

  return { year: Number(match[1]), month: Number(match[2]) - 1 }
}

export function shiftMonth(date, offset) {
  return new Date(Date.UTC(date.year, date.month + offset, 1))
}

export function monthValue(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`
}

export function buildCalendarMonth({ month, events, variant = "schedule", hideNavigation = false, compact = false }) {
  const year = month.getUTCFullYear()
  const monthIndex = month.getUTCMonth()
  const firstOfMonth = new Date(Date.UTC(year, monthIndex, 1))
  const lastOfMonth = new Date(Date.UTC(year, monthIndex + 1, 0))
  const leadingDays = (firstOfMonth.getUTCDay() + 6) % 7
  const trailingDays = (7 - ((lastOfMonth.getUTCDay() + 6) % 7) - 1) % 7
  const rangeStart = new Date(firstOfMonth.getTime() - leadingDays * DAY_MS)
  const totalDays = leadingDays + lastOfMonth.getUTCDate() + trailingDays
  const locale = document.documentElement.lang || "en"
  const monthLabel = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(firstOfMonth)
  const weekdayLabels = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(Date.UTC(2024, 0, 1 + index))
    return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(date)
  })
  const eventsByDate = groupEventsByDate(events)

  const root = document.createElement("div")
  root.className = "simple-calendar"
  root.dataset.calendarMonth = `${year}-${String(monthIndex + 1).padStart(2, "0")}`
  root.innerHTML = `
    <div class="calendar-heading flex justify-between items-center mb-6 px-4">
      ${hideNavigation ? "" : navigationLink("previous", "←")}
      <span class="calendar-title text-lg font-black text-gray-900 uppercase tracking-widest">${escapeHtml(monthLabel)}</span>
      ${hideNavigation ? "" : navigationLink("next", "→")}
    </div>
    <table class="w-full border-separate border-spacing-1">
      <thead>
        <tr>${weekdayLabels.map((label) => `<th class="text-[10px] font-black text-gray-400 uppercase tracking-tighter pb-4">${escapeHtml(label)}</th>`).join("")}</tr>
      </thead>
      <tbody></tbody>
    </table>`

  const tbody = root.querySelector("tbody")
  for (let index = 0; index < totalDays; index += 7) {
    const row = document.createElement("tr")
    for (let dayOffset = 0; dayOffset < 7; dayOffset += 1) {
      const date = new Date(rangeStart.getTime() + (index + dayOffset) * DAY_MS)
      const dateKey = isoDate(date)
      const dayEvents = eventsByDate.get(dateKey) || []
      const cell = document.createElement("td")
      const monthClass = date.getUTCMonth() === monthIndex
        ? "current-month"
        : date < firstOfMonth ? "prev-month" : "next-month"
      cell.className = `day ${monthClass} ${dateKey === todayIso() ? "today" : ""} ${dayEvents.length ? "has-events" : ""} aspect-square border border-gray-100 rounded-xl relative hover:bg-gray-50 transition-colors cursor-default`
      cell.innerHTML = variant === "service"
        ? serviceDayHtml(date, dateKey, dayEvents, compact)
        : scheduleDayHtml(date, dateKey, dayEvents)
      row.appendChild(cell)
    }
    tbody.appendChild(row)
  }

  return root
}

function serviceDayHtml(date, dateKey, events, compact) {
  const desktopSize = compact ? "md:min-h-[4rem] md:p-1.5" : "md:min-h-[5.5rem] md:p-2"
  const dotSize = compact ? "" : "md:h-2 md:w-2"
  return `
    <button type="button"
            data-action="click->calendar-modal#showDay"
            data-date="${dateKey}"
            data-calendar-modal-target="dateCell"
            class="flex min-h-[3.25rem] w-full cursor-pointer flex-col items-center rounded-lg p-1 text-left transition hover:bg-blue-50 md:items-start ${desktopSize}">
      <span data-calendar-day-number class="text-[9px] font-black ${dateKey === todayIso() ? "text-blue-600" : "text-gray-500"} md:text-[10px]">${date.getUTCDate()}</span>
      ${events.length ? `<span class="mt-auto flex flex-wrap justify-center gap-1 self-center pb-1 md:self-start">${events.slice(0, 3).map((event) => `<span class="h-1.5 w-1.5 rounded-full ${dotClass(event)} ${dotSize}"></span>`).join("")}</span>` : ""}
    </button>`
}

function scheduleDayHtml(date, dateKey, events) {
  const today = dateKey === todayIso()
  return `
    <div class="min-h-[3.25rem] md:min-h-[100px] p-1 md:p-2 flex flex-col gap-0.5 md:gap-1 md:overflow-y-auto no-scrollbar cursor-pointer rounded-lg md:rounded-none"
         data-action="click->calendar-modal#openAtDate"
         data-date="${dateKey}"
         data-calendar-modal-target="dateCell">
      <div class="text-[9px] md:text-[10px] font-black text-center md:text-left ${today ? "text-blue-600 ring-2 ring-blue-500 ring-offset-1 rounded-full w-6 h-6 flex items-center justify-center mx-auto md:mx-0 md:ring-0 md:w-auto md:h-auto md:rounded-none" : "text-gray-400"}">${date.getUTCDate()}</div>
      ${events.slice().sort(eventSort).map((event) => `
        <button type="button"
                data-action="click->calendar-modal#showDay"
                data-date="${dateKey}"
                class="hidden md:block w-full p-1.5 rounded-lg text-left transition transform hover:scale-[1.02] active:scale-95 ${cardClass(event)}">
          <span class="text-[8px] font-black truncate block ${textClass(event)} uppercase tracking-tighter">${escapeHtml(event.title || "Session")}</span>
          <span class="text-[7px] font-bold text-gray-400 mt-0.5 block">${escapeHtml(event.display_time || "")}</span>
        </button>`).join("")}
      ${events.length ? `
        <div class="md:hidden flex items-center justify-center gap-0.5 flex-wrap mt-auto pt-0.5" aria-hidden="true">
          ${events.slice(0, 3).map((event) => `<span class="w-1.5 h-1.5 rounded-full ${dotClass(event)}"></span>`).join("")}
          ${events.length > 3 ? `<span class="text-[7px] font-black text-gray-400 leading-none">+${events.length - 3}</span>` : ""}
        </div>` : ""}
    </div>`
}

function navigationLink(direction, label) {
  return `<a href="#" data-calendar-direction="${direction}" class="text-gray-400 hover:text-blue-600 font-black text-xl p-2">${label}</a>`
}

function groupEventsByDate(events) {
  return events.reduce((grouped, event) => {
    const date = String(event.calendar_date || event.start_time || "").slice(0, 10)
    if (!date) return grouped
    if (!grouped.has(date)) grouped.set(date, [])
    grouped.get(date).push(event)
    return grouped
  }, new Map())
}

function eventColor(event) {
  return event.calendar_color || (event.is_full ? "red" : "blue")
}

function cardClass(event) {
  return {
    red: "bg-red-50 border border-red-100",
    green: "bg-green-50 border border-green-100",
    blue: "bg-blue-50 border border-blue-100"
  }[eventColor(event)] || "bg-blue-50 border border-blue-100"
}

function textClass(event) {
  return {
    red: "text-red-700",
    green: "text-green-700",
    blue: "text-blue-700"
  }[eventColor(event)] || "text-blue-700"
}

function dotClass(event) {
  return {
    red: "bg-red-500",
    green: "bg-green-500",
    blue: "bg-blue-500"
  }[eventColor(event)] || "bg-blue-500"
}

function eventSort(left, right) {
  return String(left.start_time || left.display_time || "").localeCompare(String(right.start_time || right.display_time || ""))
}

function isoDate(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`
}

function todayIso() {
  const today = new Date()
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
}

function escapeHtml(value) {
  const element = document.createElement("div")
  element.textContent = String(value)
  return element.innerHTML
}
